import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Upload, CheckSquare, Cpu, ShieldCheck, FileText, BarChart3, History, LogOut, Bell, Menu, X, Landmark, List } from 'lucide-react';
export const Layout: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user,
    logout
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const navItems = [{
    path: '/',
    label: 'Dashboard',
    icon: LayoutDashboard,
    roles: ['operator', 'officer', 'zso', 'admin']
  }, {
    path: '/upload',
    label: 'Data Submission',
    icon: Upload,
    roles: ['operator', 'zso']
  }, {
    path: '/submissions',
    label: 'All Submissions',
    icon: List,
    roles: ['operator', 'officer', 'zso', 'admin']
  }, {
    path: '/validate',
    label: 'Validation',
    icon: CheckSquare,
    roles: ['officer']
  }, {
    path: '/processing',
    label: 'Processing Status',
    icon: Cpu,
    roles: ['officer', 'admin']
  }, {
    path: '/approve',
    label: 'Verification & Approval',
    icon: ShieldCheck,
    roles: ['admin']
  }, {
    path: '/viewer',
    label: 'Khatian Viewer',
    icon: FileText,
    roles: ['operator', 'officer', 'zso', 'admin']
  }, {
    path: '/reports',
    label: 'Reports',
    icon: BarChart3,
    roles: ['officer', 'zso', 'admin']
  }, {
    path: '/audit',
    label: 'Audit Trail',
    icon: History,
    roles: ['admin']
  }];
  const filteredNavItems = navItems.filter(item => user && item.roles.includes(user.role));
  return <div className="min-h-screen bg-slate-50 flex" data-id="element-150">
      {/* Sidebar */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col fixed h-full z-20`} data-id="element-151">
        <div className="h-16 flex items-center justify-center border-b border-slate-800 px-4" data-id="element-152">
          <Landmark className="text-teal-500 min-w-[24px]" size={24} data-id="element-153" />
          {isSidebarOpen && <div className="ml-3 font-semibold text-sm leading-tight" data-id="element-154">
              Survey Khatian
              <br data-id="element-155" />
              <span className="text-teal-400 font-normal text-xs" data-id="element-156">
                Publishing (DLRS)
              </span>
            </div>}
        </div>

        <div className="flex-1 overflow-y-auto py-4" data-id="element-157">
          <nav className="space-y-1 px-2" data-id="element-158">
            {filteredNavItems.map(item => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={`flex items-center px-3 py-2.5 rounded-md transition-colors ${isActive ? 'bg-teal-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`} title={!isSidebarOpen ? item.label : undefined} data-id="element-159">
                  <Icon size={20} className="min-w-[20px]" data-id="element-160" />
                  {isSidebarOpen && <span className="ml-3 text-sm font-medium" data-id="element-161">
                      {item.label}
                    </span>}
                </Link>;
          })}
          </nav>
        </div>

        <div className="p-4 border-t border-slate-800" data-id="element-162">
          <div className={`flex items-center ${isSidebarOpen ? 'justify-between' : 'justify-center'}`} data-id="element-163">
            {isSidebarOpen && <div className="flex flex-col overflow-hidden" data-id="element-164">
                <span className="text-sm font-medium truncate" data-id="element-165">
                  {user?.name}
                </span>
                <span className="text-xs text-slate-400 truncate" data-id="element-166">
                  {user?.designation}
                </span>
              </div>}
            <button onClick={handleLogout} className="text-slate-400 hover:text-white transition-colors p-1 rounded-md hover:bg-slate-800" title="Logout" data-id="element-167">
              <LogOut size={18} data-id="element-168" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`} data-id="element-169">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-10" data-id="element-170">
          <div className="flex items-center" data-id="element-171">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 rounded-md text-slate-500 hover:bg-slate-100 focus:outline-none" data-id="element-172">
              {isSidebarOpen ? <X size={20} data-id="element-173" /> : <Menu size={20} data-id="element-174" />}
            </button>
            <div className="ml-4 text-sm font-medium text-slate-800" data-id="element-175">
              {filteredNavItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </div>
          </div>

          <div className="flex items-center space-x-4" data-id="element-176">
            <button className="relative p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors" data-id="element-177">
              <Bell size={20} data-id="element-178" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white" data-id="element-179"></span>
            </button>
            <div className="h-8 w-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-sm border border-teal-200" data-id="element-180">
              {user?.name.charAt(0)}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto" data-id="element-181">{children}</main>
      </div>
    </div>;
};